import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Star, Archive, Trash2, MoreVertical, Reply, ReplyAll,
  Forward, Paperclip, Download, Tag, Clock, FolderInput,
  Mail, MailOpen, Bookmark, BookmarkX, BellOff, Filter,
  CalendarPlus, Printer, FileDown, Code2, Languages,
  ShieldX, AlertOctagon, Inbox, Send, FileText,
  AlertTriangle, Users2, BellRing, MessagesSquare, Check,
  ChevronRight, X, Eye,
} from 'lucide-react'
import DOMPurify from 'dompurify'
import { format, parseISO, addHours, nextMonday, nextFriday, setHours, setMinutes } from 'date-fns'
import { useMailStore } from '../../stores/mailStore'
import { useUIStore } from '../../stores/uiStore'
import { useAccountStore } from '../../stores/accountStore'
import { useTrashThread } from '../../hooks/useTrashThread'
import { useRestoreThread } from '../../hooks/useRestoreThread'
import { useMarkThreadRead } from '../../hooks/useMarkThreadRead'
import { useStarThread } from '../../hooks/useStarThread'
import { useMarkImportant } from '../../hooks/useMarkImportant'
import type { Thread, Message, Label } from '../../lib/types'
import { clsx } from 'clsx'
import { AttachmentPreviewModal } from './AttachmentPreviewModal'

// ─── Portal Dropdown ──────────────────────────────────────────────────────────

interface DropdownMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  children: React.ReactNode
  align?: 'left' | 'right'
  width?: number
}

function DropdownMenu({ anchorEl, open, onClose, children, align = 'right', width = 224 }: DropdownMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    // Defer so the current click event doesn't immediately close the menu
    const id = setTimeout(() => {
      const onMouseDown = (e: MouseEvent) => {
        if (!menuRef.current?.contains(e.target as Node)) onClose()
      }
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
      document.addEventListener('mousedown', onMouseDown)
      document.addEventListener('keydown', onKey)
      return () => {
        document.removeEventListener('mousedown', onMouseDown)
        document.removeEventListener('keydown', onKey)
      }
    }, 0)
    return () => clearTimeout(id)
  }, [open, onClose])

  if (!open || !anchorEl) return null

  const rect = anchorEl.getBoundingClientRect()
  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + 4,
    width,
    zIndex: 9000,
  }
  if (align === 'right') {
    style.right = Math.max(8, window.innerWidth - rect.right)
  } else {
    style.left = Math.max(8, rect.left)
  }

  return createPortal(
    <motion.div
      ref={menuRef}
      className="dm"
      style={style}
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -4, scale: 0.97 }}
      transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>,
    document.body
  )
}

function DmItem({
  icon: Icon, label, onClick, danger, disabled, checked,
}: {
  icon?: React.ElementType
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  checked?: boolean
}) {
  return (
    <button
      className={`dm-item${danger ? ' dm-item--danger' : ''}${disabled ? ' dm-item--disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
    >
      {Icon && <Icon size={14} className="dm-item__icon" />}
      <span>{label}</span>
      {checked !== undefined && checked && <Check size={12} className="dm-item__check" />}
    </button>
  )
}

function DmDivider() {
  return <div className="dm-divider" />
}

function DmLabel({ label }: { label: string }) {
  return <div className="dm-section-label">{label}</div>
}

// ─── Snooze menu ──────────────────────────────────────────────────────────────

function snoozeOptions() {
  const now = new Date()
  const tomorrow = addHours(setHours(setMinutes(now, 0), 9), 24)
  const later = addHours(now, 4)
  const friday = setHours(setMinutes(nextFriday(now), 0), 9)
  const monday = setHours(setMinutes(nextMonday(now), 0), 9)

  return [
    { label: 'Later today', sub: format(later, 'h:mm a'),   getDate: () => later },
    { label: 'Tomorrow morning', sub: format(tomorrow, 'EEE, h:mm a'), getDate: () => tomorrow },
    { label: 'This weekend', sub: format(friday, 'EEE, h:mm a'),  getDate: () => friday },
    { label: 'Next week',    sub: format(monday, 'EEE, h:mm a'),  getDate: () => monday },
  ]
}

interface SnoozeMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onSnooze: (date: Date) => void
}

function SnoozeMenu({ anchorEl, open, onClose, onSnooze }: SnoozeMenuProps) {
  const options = snoozeOptions()
  return (
    <DropdownMenu anchorEl={anchorEl} open={open} onClose={onClose} width={240}>
      <DmLabel label="Snooze until..." />
      {options.map((opt) => (
        <button key={opt.label} className="dm-item dm-item--two-line" onClick={() => { onSnooze(opt.getDate()); onClose() }}>
          <Clock size={14} className="dm-item__icon" />
          <span className="dm-item__main">{opt.label}</span>
          <span className="dm-item__sub">{opt.sub}</span>
        </button>
      ))}
    </DropdownMenu>
  )
}

// ─── Move-to menu ─────────────────────────────────────────────────────────────

const MOVE_FOLDERS = [
  { slug: 'inbox',      name: 'Inbox',    icon: Inbox },
  { slug: 'starred',    name: 'Starred',  icon: Star },
  { slug: 'sent',       name: 'Sent',     icon: Send },
  { slug: 'drafts',     name: 'Drafts',   icon: FileText },
  { slug: 'spam',       name: 'Spam',     icon: AlertTriangle },
  { slug: 'trash',      name: 'Trash',    icon: Trash2 },
  { slug: 'social',     name: 'Social',   icon: Users2 },
  { slug: 'updates',    name: 'Updates',  icon: BellRing },
  { slug: 'forums',     name: 'Forums',   icon: MessagesSquare },
]

interface MoveToMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  currentSlugs: string[]
  onMove: (slug: string) => void
}

function MoveToMenu({ anchorEl, open, onClose, currentSlugs, onMove }: MoveToMenuProps) {
  return (
    <DropdownMenu anchorEl={anchorEl} open={open} onClose={onClose} width={200}>
      <DmLabel label="Move to" />
      {MOVE_FOLDERS.map(({ slug, name, icon: Icon }) => (
        <DmItem
          key={slug}
          icon={Icon}
          label={name}
          checked={currentSlugs.includes(slug)}
          onClick={() => { onMove(slug); onClose() }}
        />
      ))}
    </DropdownMenu>
  )
}

// ─── Label picker ─────────────────────────────────────────────────────────────

interface LabelMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  labels: Label[]
  threadLabelIds: string[]
  onToggle: (labelId: string) => void
}

function LabelMenu({ anchorEl, open, onClose, labels, threadLabelIds, onToggle }: LabelMenuProps) {
  const [query, setQuery] = useState('')
  const userLabels = labels.filter((l) => !l.is_system)
  const filtered = userLabels.filter((l) =>
    !query || l.name.toLowerCase().includes(query.toLowerCase())
  )

  return (
    <DropdownMenu anchorEl={anchorEl} open={open} onClose={onClose} width={220}>
      <div className="dm-search">
        <input
          autoFocus
          className="dm-search__input"
          placeholder="Search labels..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <DmDivider />
      {filtered.length === 0 && <div className="dm-empty">No labels</div>}
      {filtered.map((l) => (
        <button
          key={l.id}
          className="dm-item dm-item--label"
          onClick={() => onToggle(l.id)}
        >
          <span className="dm-label-dot" style={{ background: l.color }} />
          <span style={{ flex: 1 }}>{l.name}</span>
          {threadLabelIds.includes(l.id) && <Check size={12} />}
        </button>
      ))}
      <DmDivider />
      <DmItem icon={Tag} label="Create new label" onClick={() => onClose()} />
    </DropdownMenu>
  )
}

// ─── Thread More menu ─────────────────────────────────────────────────────────

interface ThreadMoreMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  thread: Thread
  isRead: boolean
  onMarkRead: (v: boolean) => void
  onMarkImportant: (v: boolean) => void
  onMute: () => void
}

function ThreadMoreMenu({ anchorEl, open, onClose, thread, isRead, onMarkRead, onMarkImportant, onMute }: ThreadMoreMenuProps) {
  const { addToast } = useUIStore()
  const starThread = useStarThread()
  const toast = (msg: string) => { addToast(msg, 'info'); onClose() }

  return (
    <DropdownMenu anchorEl={anchorEl} open={open} onClose={onClose} width={232}>
      <DmItem
        icon={isRead ? Mail : MailOpen}
        label={isRead ? 'Mark as unread' : 'Mark as read'}
        onClick={() => { onMarkRead(!isRead); onClose() }}
      />
      <DmItem
        icon={thread.is_important ? BookmarkX : Bookmark}
        label={thread.is_important ? 'Mark as not important' : 'Mark as important'}
        onClick={() => { onMarkImportant(!thread.is_important); onClose() }}
      />
      <DmItem
        icon={Star}
        label={thread.is_starred ? 'Remove star' : 'Add star'}
        onClick={() => {
          starThread(thread.id, !thread.is_starred)
          onClose()
        }}
      />
      <DmDivider />
      <DmItem icon={CalendarPlus} label="Create event"          onClick={() => toast('Opening calendar...')} />
      <DmItem icon={Filter}       label="Filter messages like these" onClick={() => toast('Filter created')} />
      <DmItem icon={BellOff}      label="Mute"                  onClick={() => { onMute(); onClose() }} />
    </DropdownMenu>
  )
}

// ─── Show Original modal ─────────────────────────────────────────────────────

function ShowOriginalModal({ fetchContent, onClose }: { fetchContent: () => Promise<string>; onClose: () => void }) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError]     = useState(false)

  useEffect(() => {
    fetchContent()
      .then(setContent)
      .catch(() => setError(true))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return createPortal(
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="show-original-modal">
        <div className="show-original-modal__header">
          <span>Original message</span>
          <button className="btn-icon" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="show-original-modal__body">
          {error   ? <p style={{ color: 'var(--red)', padding: 16 }}>Failed to load raw message.</p>
           : !content ? <p style={{ color: 'var(--text-secondary)', padding: 16 }}>Loading…</p>
           : <pre className="show-original-modal__pre">{content}</pre>}
        </div>
      </div>
    </>,
    document.body,
  )
}

// ─── Message More menu ────────────────────────────────────────────────────────

interface MessageMoreMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  message: Message
  onReply: () => void
  onForward: () => void
  onDelete: () => void
  onMarkUnread: () => void
  onShowOriginal: () => void
}

function printMessage(message: Message) {
  const from = message.from_name
    ? `${message.from_name} &lt;${message.from_address}&gt;`
    : message.from_address
  const to = (message.to_addresses ?? [])
    .map(a => a.name ? `${a.name} &lt;${a.address}&gt;` : a.address)
    .join(', ')
  const date = message.date
    ? new Date(message.date).toLocaleString()
    : ''
  const body = message.body_html
    || (message.body_text ?? '').replace(/\n/g, '<br>')
    || '<em>(no content)</em>'

  const win = window.open('', '_blank', 'width=800,height=600')
  if (!win) return
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${message.subject ?? '(no subject)'}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; color: #000; margin: 32px; }
    .header { border-bottom: 1px solid #ccc; padding-bottom: 12px; margin-bottom: 16px; }
    .header-row { margin: 3px 0; }
    .label { color: #555; font-weight: bold; display: inline-block; width: 50px; }
    .subject { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="subject">${message.subject ?? '(no subject)'}</div>
    <div class="header-row"><span class="label">From:</span> ${from}</div>
    <div class="header-row"><span class="label">To:</span> ${to}</div>
    <div class="header-row"><span class="label">Date:</span> ${date}</div>
  </div>
  <div class="body">${body}</div>
</body>
</html>`)
  win.document.close()
  win.focus()
  // afterprint fires once the dialog is dismissed — close the helper window cleanly.
  // Using requestAnimationFrame avoids blocking the main thread (unlike win.print() in setTimeout).
  win.addEventListener('afterprint', () => win.close())
  setTimeout(() => win.print(), 250)
}

function MessageMoreMenu({ anchorEl, open, onClose, message, onReply, onForward, onDelete, onMarkUnread, onShowOriginal }: MessageMoreMenuProps) {
  const { addToast, openFilterCreator } = useUIStore()
  const senderName = message.from_name || message.from_address.split('@')[0]

  // ── Report spam / phishing: move thread to Junk via JMAP ──────────────────
  const handleSpam = () => {
    onClose()
    const client = useAccountStore.getState().getActiveClient()
    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    if (!client) { addToast('Not connected', 'error'); return }
    const junkMb = client.findMailboxByRole(mailboxes, 'junk')
    if (!junkMb) { addToast('Spam folder not found', 'error'); return }
    const removeIds = message.to_addresses ? [] : []  // thread labels not available here; no-op remove
    client.moveThreadToMailbox(message.thread_id, junkMb.id, [], { '$junk': true })
      .then(() => addToast('Moved to Spam', 'info'))
      .catch(() => addToast('Failed to move to Spam', 'error'))
  }

  // ── Download .eml ──────────────────────────────────────────────────────────
  const handleDownload = async () => {
    onClose()
    if (!message.blob_id) { addToast('Download not available', 'error'); return }
    const client = useAccountStore.getState().getActiveClient()
    if (!client) { addToast('Not connected', 'error'); return }
    try {
      const raw = await client.downloadBlob(message.blob_id)
      const blob = new Blob([raw], { type: 'message/rfc822' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(message.subject ?? 'message').replace(/[^a-z0-9]/gi, '_')}.eml`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      addToast('Download failed', 'error')
    }
  }

  // ── Translate: open Google Translate with message text ────────────────────
  const handleTranslate = () => {
    onClose()
    const text = message.body_text || message.body_html?.replace(/<[^>]+>/g, ' ') || ''
    const url = `https://translate.google.com/?sl=auto&tl=en&text=${encodeURIComponent(text.slice(0, 5000))}&op=translate`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <DropdownMenu anchorEl={anchorEl} open={open} onClose={onClose} width={240} align="right">
      <DmItem icon={Reply}   label="Reply"    onClick={() => { onReply(); onClose() }} />
      <DmItem icon={Forward} label="Forward"  onClick={() => { onForward(); onClose() }} />
      <DmDivider />
      <DmItem icon={Trash2}  label="Delete"           onClick={() => { onDelete(); onClose() }} danger />
      <DmItem icon={Mail}    label="Mark as unread"   onClick={() => { onMarkUnread(); onClose() }} />
      <DmItem icon={ShieldX} label={`Block "${senderName}"`}
        onClick={() => {
          onClose()
          openFilterCreator({ from: message.from_address })
          // Filter creator will be pre-filled; user can choose to delete/skip inbox
        }} danger />
      <DmItem icon={AlertOctagon} label="Report spam"     onClick={handleSpam} danger />
      <DmItem icon={AlertOctagon} label="Report phishing" onClick={handleSpam} danger />
      <DmDivider />
      <DmItem icon={Filter}   label="Filter messages like this"
        onClick={() => { onClose(); openFilterCreator({ from: message.from_address, subject: message.subject ?? '' }) }} />
      <DmItem icon={Languages} label="Translate"   onClick={handleTranslate} />
      <DmItem icon={Printer}  label="Print"        onClick={() => { printMessage(message); onClose() }} />
      <DmItem icon={FileDown} label="Download message" onClick={handleDownload} />
      <DmItem icon={Code2}    label="Show original"    onClick={() => { onShowOriginal(); onClose() }} />
    </DropdownMenu>
  )
}

// ─── Address chip ─────────────────────────────────────────────────────────────

function AddressChip({ name, address }: { name: string; address: string }) {
  return (
    <span className="address-chip" title={address}>
      {name || address}
    </span>
  )
}

// ─── Single message in thread ─────────────────────────────────────────────────

interface MessageItemProps {
  message: Message
  isExpanded: boolean
  isLast: boolean
  onToggle: () => void
  onReply: () => void
  onReplyAll: () => void
  onForward: () => void
  onDelete: () => void
}

function MessageItem({ message, isExpanded, isLast, onToggle, onReply, onReplyAll, onForward, onDelete }: MessageItemProps) {
  const { addToast } = useUIStore()
  const markRead = useMarkThreadRead()
  const starThread = useStarThread()
  const hasMultipleRecipients =
    (message.to_addresses?.length ?? 0) + (message.cc_addresses?.length ?? 0) > 1
  const [showAllHeaders, setShowAllHeaders] = useState(false)
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const [showOriginalUrl, setShowOriginalUrl] = useState<(() => Promise<string>) | null>(null)

  const bodyHTML = message.body_html
    ? DOMPurify.sanitize(message.body_html, {
        ALLOWED_TAGS: [
          'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li',
          'h1', 'h2', 'h3', 'h4', 'blockquote', 'pre', 'code', 'span', 'div',
          'table', 'tr', 'td', 'th', 'thead', 'tbody', 'img', 'hr',
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target', 'rel'],
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
      })
    : null

  const dateFormatted = format(parseISO(message.date), 'MMM d, yyyy, h:mm a')

  return (
    <>
    <div className={clsx('message-item', {
      'message-item--expanded': isExpanded,
      'message-item--last': isLast,
      'message-item--unread': !message.is_read,
    })}>
      {/* Header */}
      <div className="message-item__header" onClick={onToggle}>
        <div className="message-item__from-row">
          <div className="message-item__avatar">
            {(message.from_name || message.from_address)[0].toUpperCase()}
          </div>
          <div className="message-item__from-info">
            <div className="message-item__from-name">
              {message.from_name || message.from_address}
              {isExpanded && (
                <span className="message-item__from-email"> &lt;{message.from_address}&gt;</span>
              )}
            </div>
            {isExpanded && (
              <button
                className="message-item__detail-toggle"
                onClick={(e) => { e.stopPropagation(); setShowAllHeaders((v) => !v) }}
              >
                to me
                {showAllHeaders ? <ChevronRight size={11} style={{ transform: 'rotate(90deg)' }} /> : <ChevronRight size={11} />}
              </button>
            )}
            {!isExpanded && (
              <span className="message-item__snippet">{message.snippet}</span>
            )}
          </div>
          <div className="message-item__header-right" onClick={(e) => e.stopPropagation()}>
            <span className="message-item__date">{dateFormatted}</span>
            {isExpanded && (
              <>
                <button className="btn-icon" title="Star" onClick={() => starThread(message.thread_id, !message.is_starred)}>
                  <Star size={14} fill={message.is_starred ? 'var(--star-color)' : 'none'} color={message.is_starred ? 'var(--star-color)' : 'currentColor'} />
                </button>
                <button className="btn-icon" title="Reply" onClick={onReply}>
                  <Reply size={14} />
                </button>
                {hasMultipleRecipients && (
                  <button className="btn-icon" title="Reply all" onClick={onReplyAll}>
                    <ReplyAll size={14} />
                  </button>
                )}
                <button
                  className="btn-icon"
                  title="More"
                  onClick={(e) => setMoreAnchor(moreAnchor ? null : e.currentTarget)}
                >
                  <MoreVertical size={14} />
                </button>
                <MessageMoreMenu
                  anchorEl={moreAnchor}
                  open={!!moreAnchor}
                  onClose={() => setMoreAnchor(null)}
                  message={message}
                  onReply={onReply}
                  onForward={onForward}
                  onDelete={onDelete}
                  onMarkUnread={() => {
                    markRead(message.thread_id, false)
                    addToast('Marked as unread', 'info')
                  }}
                  onShowOriginal={() => {
                    if (!message.blob_id) { addToast('Raw source not available', 'error'); return }
                    const client = useAccountStore.getState().getActiveClient()
                    if (!client) { addToast('JMAP client not connected', 'error'); return }
                    // Store a bound fetch function so auth is baked in
                    setShowOriginalUrl(() => () => client.downloadBlob(message.blob_id!))
                  }}
                />
                {showOriginalUrl && (
                  <ShowOriginalModal
                    fetchContent={showOriginalUrl}
                    onClose={() => setShowOriginalUrl(null)}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Full headers */}
        <AnimatePresence>
          {isExpanded && showAllHeaders && (
            <motion.div
              className="message-item__headers-full"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
            >
              <div className="message-item__header-row">
                <span className="message-item__header-label">from</span>
                <AddressChip name={message.from_name} address={message.from_address} />
              </div>
              {message.to_addresses.length > 0 && (
                <div className="message-item__header-row">
                  <span className="message-item__header-label">to</span>
                  <div className="flex gap-1 flex-wrap">
                    {message.to_addresses.map((a, i) => (
                      <AddressChip key={i} name={a.name} address={a.address} />
                    ))}
                  </div>
                </div>
              )}
              {message.cc_addresses.length > 0 && (
                <div className="message-item__header-row">
                  <span className="message-item__header-label">cc</span>
                  <div className="flex gap-1 flex-wrap">
                    {message.cc_addresses.map((a, i) => (
                      <AddressChip key={i} name={a.name} address={a.address} />
                    ))}
                  </div>
                </div>
              )}
              <div className="message-item__header-row">
                <span className="message-item__header-label">date</span>
                <span className="text-xs text-muted">{dateFormatted}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Body */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="message-item__body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            {bodyHTML ? (
              <div className="email-body" dangerouslySetInnerHTML={{ __html: bodyHTML }} />
            ) : (
              <pre className="email-body" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {message.body_text ?? message.snippet}
              </pre>
            )}

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="message-item__attachments">
                <div className="message-item__attachments-header">
                  <Paperclip size={12} />
                  {message.attachments.length} attachment{message.attachments.length !== 1 ? 's' : ''}
                </div>
                <div className="message-item__attachments-list">
                  {message.attachments.map((att, i) => (
                    <div
                      key={att.id}
                      className="attachment-chip attachment-chip--clickable"
                      onClick={() => setPreviewIndex(i)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPreviewIndex(i) }}
                    >
                      <span className="attachment-chip__icon">
                        {att.content_type.startsWith('image/') ? '🖼' :
                         att.content_type === 'application/pdf' ? '📄' :
                         att.filename.match(/\.xlsx?$/i) ? '📊' :
                         att.filename.match(/\.docx?$/i) ? '📝' : '📎'}
                      </span>
                      <span className="attachment-chip__name">{att.filename}</span>
                      <span className="attachment-chip__size">{formatBytes(att.size_bytes)}</span>
                      <button
                        className="btn-icon attachment-chip__preview"
                        title="Preview"
                        onClick={(e) => { e.stopPropagation(); setPreviewIndex(i) }}
                      >
                        <Eye size={12} />
                      </button>
                      <button
                        className="btn-icon"
                        title="Download"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply bar */}
            {isLast && (
              <div className="message-item__reply-bar">
                <button className="btn btn-ghost btn-sm" onClick={onReply}>
                  <Reply size={13} /> Reply
                </button>
                {hasMultipleRecipients && (
                  <button className="btn btn-ghost btn-sm" onClick={onReplyAll}>
                    <ReplyAll size={13} /> Reply all
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={onForward}>
                  <Forward size={13} /> Forward
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>

    {/* Attachment preview modal */}
    {previewIndex !== null && message.attachments && message.attachments.length > 0 && (
      <AttachmentPreviewModal
        attachments={message.attachments}
        initialIndex={previewIndex}
        onClose={() => setPreviewIndex(null)}
        onDownload={(att) => {
          addToast(`Downloading ${att.filename}…`, 'info')
          setPreviewIndex(null)
        }}
      />
    )}
    </>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Thread View ──────────────────────────────────────────────────────────────

export function ThreadView({ thread }: { thread: Thread }) {
  const {
    labels, archiveThread,
    snoozeThread, openCompose, setActiveThread, activeFolder,
  } = useMailStore()
  const { addToast } = useUIStore()
  const trashThread = useTrashThread()
  const restoreThread = useRestoreThread()
  const markRead = useMarkThreadRead()
  const starThread = useStarThread()
  const markImportant = useMarkImportant()
  const isTrashed = activeFolder === 'trash'

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const msgs = thread.messages ?? []
    if (msgs.length === 0) return new Set()
    return new Set([msgs[msgs.length - 1].id])
  })

  // When messages load asynchronously (fetched after initial render), expand the last one
  useEffect(() => {
    const msgs = thread.messages ?? []
    if (msgs.length === 0) return
    setExpandedIds((prev) => {
      if (prev.size > 0) return prev // already has an expanded message
      return new Set([msgs[msgs.length - 1].id])
    })
  }, [thread.messages])

  // Toolbar dropdown anchors
  const [snoozeAnchor,  setSnoozeAnchor]  = useState<HTMLElement | null>(null)
  const [moveAnchor,    setMoveAnchor]    = useState<HTMLElement | null>(null)
  const [labelAnchor,   setLabelAnchor]   = useState<HTMLElement | null>(null)
  const [moreAnchor,    setMoreAnchor]    = useState<HTMLElement | null>(null)

  const isRead = (thread.messages ?? []).every((m) => m.is_read)
    || thread.unread_count === 0

  const toggleMessage = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleReply = (message: Message) => {
    openCompose({
      to: [{ name: message.from_name, address: message.from_address }],
      subject: `Re: ${thread.subject}`,
      reply_to_thread_id: thread.id,
      reply_to_message_id: message.id,
    })
  }

  const handleReplyAll = (message: Message) => {
    const myEmail = useMailStore.getState().user?.email
    const allRecipients = [
      { name: message.from_name, address: message.from_address },
      ...(thread.messages
        ?.flatMap((m) => m.to_addresses ?? [])
        .filter((addr) => addr.address !== myEmail)
        .map((addr) => ({ name: addr.name, address: addr.address })) ?? []),
    ]
    openCompose({
      to: allRecipients,
      subject: `Re: ${thread.subject}`,
      reply_to_thread_id: thread.id,
      reply_to_message_id: message.id,
    })
  }

  const handleForward = (message: Message) => {
    openCompose({
      subject: `Fwd: ${thread.subject}`,
      body_html: `<br/><br/>---------- Forwarded message ---------<br/>From: ${message.from_name} &lt;${message.from_address}&gt;<br/>${message.body_html ?? message.snippet}`,
    })
  }

  const handleArchive = () => {
    archiveThread(thread.id)
    addToast('Archived', 'success', {
      action: { label: 'Undo', fn: () => addToast('Undo not implemented yet', 'info') },
    })
  }

  const handleTrash = () => {
    trashThread(thread)
    addToast('Moved to Trash', 'info', {
      action: { label: 'Undo', fn: () => addToast('Undo not implemented yet', 'info') },
    })
  }

  const handleSnooze = (until: Date) => {
    snoozeThread(thread.id, until)
    addToast(`Snoozed until ${format(until, 'EEE, MMM d, h:mm a')}`, 'success')
  }

  const handleMove = (slug: string) => {
    addToast(`Moved to ${slug.replace('_', ' ')}`, 'success')
  }

  const handleToggleLabel = (labelId: string) => {
    addToast('Label updated', 'success')
  }

  const messages = thread.messages ?? []
  const currentLabelSlugs = thread.labels.map((l) => l.slug)
  const currentLabelIds   = thread.labels.map((l) => l.id)

  return (
    <div className="thread-view">
      {/* ── Toolbar ── */}
      <div className="thread-view__toolbar">
        <div className="thread-view__toolbar-left">
          <button className="btn-icon" onClick={() => setActiveThread(null)} title="Back">
            <ArrowLeft size={16} />
          </button>

          <div className="thread-view__toolbar-divider" />

          {isTrashed ? (
            <button
              className="btn-icon"
              onClick={() => { restoreThread(thread); addToast('Moved to Inbox', 'success') }}
              title="Move to Inbox"
            >
              <Inbox size={16} />
            </button>
          ) : (
            <>
              <button
                className="btn-icon"
                onClick={handleArchive}
                title="Archive (e)"
              >
                <Archive size={16} />
              </button>

              <button
                className="btn-icon"
                onClick={() => addToast('Marked as spam', 'info')}
                title="Report spam"
              >
                <AlertTriangle size={16} />
              </button>

              <button
                className="btn-icon"
                onClick={handleTrash}
                title="Delete (#)"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}

          <div className="thread-view__toolbar-divider" />

          <button
            className="btn-icon"
            onClick={() => { markRead(thread.id, isRead ? false : true); addToast(isRead ? 'Marked as unread' : 'Marked as read', 'info') }}
            title={isRead ? 'Mark as unread' : 'Mark as read'}
          >
            {isRead ? <Mail size={16} /> : <MailOpen size={16} />}
          </button>

          {/* Snooze */}
          <button
            className={`btn-icon${snoozeAnchor ? ' btn-icon--active' : ''}`}
            onClick={(e) => setSnoozeAnchor(snoozeAnchor ? null : e.currentTarget)}
            title="Snooze"
          >
            <Clock size={16} />
          </button>
          <SnoozeMenu
            anchorEl={snoozeAnchor}
            open={!!snoozeAnchor}
            onClose={() => setSnoozeAnchor(null)}
            onSnooze={handleSnooze}
          />

          <div className="thread-view__toolbar-divider" />

          {/* Move to */}
          <button
            className={`btn-icon${moveAnchor ? ' btn-icon--active' : ''}`}
            onClick={(e) => setMoveAnchor(moveAnchor ? null : e.currentTarget)}
            title="Move to"
          >
            <FolderInput size={16} />
          </button>
          <MoveToMenu
            anchorEl={moveAnchor}
            open={!!moveAnchor}
            onClose={() => setMoveAnchor(null)}
            currentSlugs={currentLabelSlugs}
            onMove={handleMove}
          />

          {/* Labels */}
          <button
            className={`btn-icon${labelAnchor ? ' btn-icon--active' : ''}`}
            onClick={(e) => setLabelAnchor(labelAnchor ? null : e.currentTarget)}
            title="Labels"
          >
            <Tag size={16} />
          </button>
          <LabelMenu
            anchorEl={labelAnchor}
            open={!!labelAnchor}
            onClose={() => setLabelAnchor(null)}
            labels={labels}
            threadLabelIds={currentLabelIds}
            onToggle={handleToggleLabel}
          />

          {/* More */}
          <button
            className={`btn-icon${moreAnchor ? ' btn-icon--active' : ''}`}
            onClick={(e) => setMoreAnchor(moreAnchor ? null : e.currentTarget)}
            title="More actions"
          >
            <MoreVertical size={16} />
          </button>
          <ThreadMoreMenu
            anchorEl={moreAnchor}
            open={!!moreAnchor}
            onClose={() => setMoreAnchor(null)}
            thread={thread}
            isRead={isRead}
            onMarkRead={(v) => { markRead(thread.id, v); addToast(v ? 'Marked as read' : 'Marked as unread', 'info') }}
            onMarkImportant={(v) => { markImportant(thread.id, v); addToast(v ? 'Marked as important' : 'Removed from important', 'info') }}
            onMute={() => addToast('Thread muted', 'info')}
          />
        </div>

        <div className="thread-view__toolbar-right">
          {messages.length > 0 && (
            <span className="text-xs text-muted font-mono">
              {messages.length} of {messages.length}
            </span>
          )}
        </div>
      </div>

      {/* ── Subject + label chips ── */}
      <div className="thread-view__subject-row">
        <h1 className="thread-view__subject">{thread.subject}</h1>
        <div className="thread-view__subject-tags">
          {thread.labels
            .filter((l) => !['inbox', 'sent', 'drafts', 'trash', 'spam', 'all_mail'].includes(l.slug))
            .map((label) => (
              <span
                key={label.id}
                className="thread-label-chip"
                style={{ background: label.color + '22', color: label.color, borderColor: label.color + '44' }}
              >
                {label.name}
                <button
                  className="thread-label-chip__remove"
                  onClick={() => handleToggleLabel(label.id)}
                  title="Remove label"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="thread-view__messages">
        {messages.length === 0 ? (
          <div className="thread-view__preview">
            <p className="email-body">{thread.snippet}</p>
            <div className="thread-view__participants">
              {thread.participants.map((p) => (
                <span key={p} className="address-chip">{p.split('@')[0]}</span>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03, duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <MessageItem
                message={msg}
                isExpanded={expandedIds.has(msg.id)}
                isLast={idx === messages.length - 1}
                onToggle={() => toggleMessage(msg.id)}
                onReply={() => handleReply(msg)}
                onReplyAll={() => handleReplyAll(msg)}
                onForward={() => handleForward(msg)}
                onDelete={() => { trashThread(thread); addToast('Moved to Trash', 'info') }}
              />
            </motion.div>
          ))
        )}
      </div>

      <style>{`
        /* ── Thread view shell ── */
        .thread-view {
          flex: 1;
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
          background: var(--bg-root);
        }

        .thread-view__toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 12px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
          min-height: 44px;
          gap: 2px;
          background: var(--bg-panel);
        }

        .thread-view__toolbar-left {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .thread-view__toolbar-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .thread-view__toolbar-divider {
          width: 1px;
          height: 20px;
          background: var(--border);
          margin: 0 4px;
          flex-shrink: 0;
        }

        .btn-icon--active {
          background: var(--bg-selected-strong) !important;
          color: var(--text-primary) !important;
        }

        .thread-view__subject-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 16px 24px 10px;
          flex-wrap: wrap;
          flex-shrink: 0;
          border-bottom: 1px solid var(--border);
        }

        .thread-view__subject {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.3;
          letter-spacing: -0.2px;
          flex: 1;
          min-width: 0;
        }

        .thread-view__subject-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
        }

        .thread-label-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px 2px 10px;
          border-radius: var(--radius-full);
          border: 1px solid transparent;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
        }

        .thread-label-chip__remove {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
          padding: 0;
        }
        .thread-label-chip__remove:hover { opacity: 1; }

        .thread-view__messages {
          flex: 1;
          overflow-y: auto;
          padding: 8px 16px 40px;
        }

        .thread-view__preview {
          padding: 24px 8px;
        }

        .thread-view__participants {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 16px;
        }

        /* ── Message items ── */
        .message-item {
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          overflow: hidden;
          background: var(--bg-surface);
          margin: 6px 0;
          transition: border-color var(--duration-fast);
        }

        .message-item--unread {
          border-left: 2px solid var(--brand);
        }

        .message-item__header {
          padding: 12px 16px 10px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: background var(--duration-fast);
        }
        .message-item__header:hover { background: var(--bg-hover); }

        .message-item__from-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .message-item__avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--brand);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .message-item__from-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          min-width: 0;
        }

        .message-item__from-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0 4px;
          min-width: 0;
        }

        .message-item__from-email {
          flex-basis: 100%;
          font-size: 12px;
          font-weight: 400;
          color: var(--text-tertiary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .message-item__detail-toggle {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-tertiary);
          font-family: var(--font-ui);
          font-size: 12px;
          padding: 0;
        }
        .message-item__detail-toggle:hover { color: var(--text-secondary); }

        .message-item__snippet {
          font-size: 12px;
          color: var(--text-tertiary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .message-item__header-right {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }

        .message-item__date {
          font-size: 11px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
          white-space: nowrap;
        }

        .message-item__headers-full {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px 0 4px 42px;
          overflow: hidden;
        }

        .message-item__header-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 12px;
        }

        .message-item__header-label {
          width: 36px;
          flex-shrink: 0;
          color: var(--text-tertiary);
          font-size: 11px;
          text-align: right;
        }

        .message-item__body {
          padding: 4px 16px 16px 58px;
          overflow: hidden;
        }

        .message-item__attachments {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid var(--border);
        }

        .message-item__attachments-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 10px;
        }

        .message-item__attachments-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .attachment-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 10px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          font-size: 12px;
          max-width: 220px;
        }

        .attachment-chip--clickable {
          cursor: pointer;
          transition: background 0.12s, border-color 0.12s;
        }

        .attachment-chip--clickable:hover {
          background: var(--bg-hover);
          border-color: var(--brand);
        }

        .attachment-chip__preview {
          opacity: 0;
          transition: opacity 0.1s;
        }

        .attachment-chip--clickable:hover .attachment-chip__preview {
          opacity: 1;
        }

        .attachment-chip__name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }

        .attachment-chip__size {
          color: var(--text-tertiary);
          font-size: 11px;
          flex-shrink: 0;
        }

        .message-item__reply-bar {
          display: flex;
          gap: 8px;
          padding-top: 20px;
          margin-top: 20px;
          border-top: 1px solid var(--border);
        }

        .address-chip {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-full);
          font-size: 12px;
          color: var(--text-secondary);
          cursor: default;
        }
        .address-chip:hover { border-color: var(--border-strong); color: var(--text-primary); }

        /* ── Dropdown menu ── */
        .dm {
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          overflow: hidden;
          padding: 4px 0;
        }

        .dm-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 8px 14px;
          border: none;
          background: transparent;
          color: var(--text-secondary);
          font-family: var(--font-ui);
          font-size: 13px;
          cursor: pointer;
          text-align: left;
          transition: background var(--duration-fast), color var(--duration-fast);
          white-space: nowrap;
        }

        .dm-item:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .dm-item--danger { color: var(--red); }
        .dm-item--danger:hover { background: var(--red-subtle); }

        .dm-item--disabled { opacity: 0.4; cursor: default; pointer-events: none; }

        .dm-item__icon {
          flex-shrink: 0;
          color: var(--text-tertiary);
        }
        .dm-item--danger .dm-item__icon { color: var(--red); }
        .dm-item:hover .dm-item__icon { color: inherit; }

        .dm-item__check {
          margin-left: auto;
          color: var(--brand);
          flex-shrink: 0;
        }

        .dm-item--two-line {
          align-items: center;
          gap: 10px;
        }

        .dm-item__main {
          flex: 1;
        }

        .dm-item__sub {
          font-size: 11px;
          color: var(--text-tertiary);
          font-family: var(--font-mono);
          flex-shrink: 0;
        }

        .dm-item--label {
          gap: 10px;
        }

        .dm-label-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .dm-divider {
          height: 1px;
          background: var(--border);
          margin: 4px 0;
        }

        .dm-section-label {
          padding: 6px 14px 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--text-tertiary);
        }

        .dm-search {
          padding: 6px 8px;
          border-bottom: 1px solid var(--border);
        }

        .dm-search__input {
          width: 100%;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 5px 8px;
          font-family: var(--font-ui);
          font-size: 12px;
          color: var(--text-primary);
          outline: none;
        }
        .dm-search__input:focus { border-color: var(--border-focus); }

        .dm-empty {
          padding: 12px 14px;
          font-size: 12px;
          color: var(--text-tertiary);
          text-align: center;
        }

        /* ── Show original modal ── */
        .show-original-modal {
          position: fixed;
          top: 5vh;
          left: 50%;
          transform: translateX(-50%);
          width: min(900px, 92vw);
          height: 88vh;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          z-index: 1600;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .show-original-modal__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .show-original-modal__body {
          flex: 1;
          overflow: auto;
          background: var(--bg-root);
        }

        .show-original-modal__pre {
          margin: 0;
          padding: 16px;
          font-family: var(--font-mono);
          font-size: 11.5px;
          line-height: 1.6;
          color: var(--text-secondary);
          white-space: pre-wrap;
          word-break: break-all;
        }
      `}</style>
    </div>
  )
}
